<template>
  <div v-if="event && ticket">
    <div>{{ event.event_name }}</div>
    <div class="lg:flex">
      <div class="w-2/2 lg:w-1/2 flex gap-1 text-base text-shoppingcart-secondary">
        <span>{{ event.tournament_name }}</span>
        <span>-</span>
        <span>{{ event.season }}</span>
      </div>

      <div class="w-2/2 lg:w-1/2 flex gap-1 text-base text-shoppingcart-secondary">
        <span>{{ event.venue_name }}</span>
        <span>{{ event.city }}</span>
      </div>
    </div>
    <div class="lg:flex">
      <div class="w-2/2 lg:w-1/2 flex gap-1 text-base text-shoppingcart-secondary">
        <span>{{ ticket.category_name }}</span>
        <span>{{
          formatDays(
            ticket.ticket_validfrom || ticket.ticket_startdate,
            ticket.ticket_validuntil || ticket.ticket_stopdate
          )
        }}</span>
      </div>
      <div class="w-2/2 lg:w-1/2 flex gap-3 text-base text-shoppingcart-secondary">
        <span v-if="event.sport_type !== 'formula1'">
          {{ formatDate(event.date_start) }}
          <span v-if="event.date_confirmed">{{ formatTime(event.date_start) }} {{ $t("confirmed") }}</span>
        </span>
        <span v-else
          >{{ formatDates(event.date_start, event.date_stop) }}
          <span v-if="event.date_confirmed">{{ $t("confirmed") }}</span>
        </span>
      </div>
    </div>
    <div v-if="paydetailsPerTicket">
      {{ formatCurrency(paydetailsPerTicket.subTotal, paydetailsPerTicket.currency) }} (
      {{ formatCurrency(paydetailsPerTicket.netRate, paydetailsPerTicket.currency) }} each )
    </div>
    <br />
    <div class="lg:flex">
      <div class="w-2/3 lg:w-2/3 flex gap-1 text-base text-shoppingcart-secondary">
        <span v-if="!event.date_confirmed">{{
          $t(
            "The date/time has not yet been confirmed by the organiser of the event. Tickets will stay valid if the current date/time changes."
          )
        }}</span>
      </div>
    </div>

    <br />
    <div v-if="category">
      <div v-if="!!TicketType(ticket.type_ticket)" class="lg:flex">
        <div class="w-2/2 lg:w-1/2 flex gap-1 text-base text-shoppingcart-secondary">
          <span
            >{{ $t("Type ticket") }}: {{ TicketType(ticket.type_ticket)
            }}<span v-if="category.sport_type === SportType.SOCCER">, {{ $t("home section") }}</span>
          </span>
        </div>
      </div>

      <div v-if="categoryHasSeats(category.category_type)" class="lg:flex">
        <div class="w-2/2 lg:w-2/2 flex gap-1 text-base text-shoppingcart-secondary">
          <span
            >{{ $t("Seating: Up to") }} {{ category.party_size_together }}
            {{ $t("seats are guaranteed to be sitting together for this event") }}.</span
          >
        </div>
      </div>

      <div class="lg:flex">
        <div class="w-2/2 lg:w-2/2 flex gap-1 text-base text-shoppingcart-secondary">
          <span v-if="category.ticket_delivery_days"
            >{{ $t("Delivery: Your tickets will be delivered at least") }}
            {{ deliveryDays(category.ticket_delivery_days) }} {{ $t("ahead of the event") }}</span
          >
        </div>
      </div>
      <div v-if="canDownloadMobileTickets" class="w-3/4 pt-4 text-base text-wrap">
        <span>{{ $t("Notice: These tickets are designed for mobile") }}</span>
      </div>
    </div>
  </div>
  <div v-else>{{ $t("Loading ticket info") }}...</div>
</template>
<script setup lang="ts">
import { ref, computed } from "vue";
import { formatDates, formatDays, formatDate, formatTime } from "@/helpers/format.js";
import { useEventStore } from "@/store/event.js";

import { deliveryDayOptions, SportType, TicketTypeOptions } from "@xs2event/js-lib";
import { useI18n } from "vue-i18n";
import type { EventResponse } from "@xs2event/js-lib/api";
import { formatCurrency } from "@xs2event/js-lib";
import { camelToSnake } from "@/helpers/casing";

const { t } = useI18n();
const props = defineProps<{
  ticket: unknown;
  ticket_id: string;
  event?: EventResponse;
  event_id?: string;
  bookingorder: unknown;
  category?: unknown;
  canDownloadMobileTickets?: boolean;
  paydetails?: unknown;
}>();

const eventStore = useEventStore();

const event = ref((props.event && camelToSnake(props.event)) || undefined);
const ticket = ref((props.ticket && camelToSnake(props.ticket)) || undefined);
const category = ref((props.category && camelToSnake(props.category)) || undefined);

const paydetailsPerTicket = computed(
  () => props.paydetails?.tickets.find((ticket) => ticket.ticketId === props.ticket_id) || null
);

const fetchEvent = async (eventId) => {
  if (event.value) return;
  event.value = await eventStore.getEvent(eventId);
  eventStore.event = event.value;
};

const fetchTicket = async (ticketId) => {
  if (ticket.value) return;
  const value = await eventStore.getTicket(ticketId);
  ticket.value = value;
  fetchEvent(value.event_id);
  fetchCategory(value.category_id);
};
const fetchCategory = async (categoryId) => {
  if (category.value) return;
  category.value = await eventStore.getCategory(categoryId);
};

if (props.ticket_id) {
  fetchTicket(props.ticket_id);
}

if (props.event_id) {
  fetchEvent(props.event_id);
}

const deliveryDays = (days) => {
  const deliverydays = deliveryDayOptions.filter((e) => e.value === days)[0];
  if (deliverydays) {
    return t(deliverydays.text);
  }
};

const TicketType = (type) => {
  return TicketTypeOptions.find((e) => e.value === type).text;
};

const categoryHasSeats = (category_type) => {
  const category_with_seats = ["grandstand", "hospitality"];
  if (category_with_seats.includes(category_type)) {
    return true;
  }
};
</script>
